import React, { useEffect, useState } from "react";
import moment from "moment";

import WebView from "./FootPrintWebView";
import { useAtom } from "jotai";
import {
  activeChildDetail,
  appConstants,
  cctvIntial,
} from "../../AtomStore/store";
import { useRequestProcessor } from "../../requestProcessor";
import {
  checkCCTVConnectionFunction,
  ReloadData,
} from "../../QueryFunctions/queryfunction";
import SpinnerLoading from "../../Components/utils/SpinnerLoading";
import { StyledCard } from "./style";

const getValidToken = (tokens = []) => {
  const nowUTC = moment.utc();
  return tokens.find(({ start, end }) => {
    const startTime = moment.utc(start);
    const endTime = moment.utc(end);
    return nowUTC.isBetween(startTime, endTime);
  })?.token;
};

const Cctv = () => {
  const [cctvState, setCctvState] = useAtom(cctvIntial);
  const [activeChild] = useAtom(activeChildDetail);
  const [constants] = useAtom(appConstants);
  const [childOnPause, setChildOnPause] = useState(false);
  const [streamError, setStreamError] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const { useMutationWrapper } = useRequestProcessor();

  // Check CCTV connection mutation
  const checkCCTVConnection = useMutationWrapper("checkCCTVConnection", () => {
    return checkCCTVConnectionFunction(cctvState, setCctvState, activeChild);
  });

  // ReloadData mutation placeholder (you can customize this or call your verifyOTP analog)
  const reloadData = useMutationWrapper("reload", () => {
    // Your reload logic here
    // For example: call ReloadData(), or call your verifyOTP wrapper
    // Here, we just simulate with ReloadData and resetting streamError to false after success
    return ReloadData().then(() => {
      setStreamError(false);
      setRefreshKey((k) => k + 1); // refresh WebView key to reload iframe
      checkCCTVConnection.mutate();
    });
  });

  // Check valid token presence & set streamError accordingly
  useEffect(() => {
    if (!activeChild) return;

    if (activeChild.pause_state === 1) {
      setChildOnPause(true);
    } else {
      setChildOnPause(false);
      checkCCTVConnection.mutate();
    }

    const validToken = getValidToken(activeChild?.cctv_access_key?.token);
    if (!validToken && !streamError) {
      console.log("[CCTV] Auth token missing — setting streamError");
      setStreamError(true);
    } else if (validToken && streamError) {
      console.log("[CCTV] Token restored — clearing streamError");
      setStreamError(false);
    }
  }, [activeChild]);

  // Compose live stream URL
  const authToken = getValidToken(activeChild?.cctv_access_key?.token);

  let liveStreamUrl = "";
  if (
    activeChild?.html5_player_link?.startsWith("https://chennaistream.net") &&
    !constants?.chennaiStream
  ) {
    liveStreamUrl = activeChild?.html5_player_link;
  } else {
    liveStreamUrl = `${activeChild?.html5_player_link}/${activeChild?.center_id}/${activeChild?.id}/${authToken}`;
  }
  console.log("livestream url ", liveStreamUrl);
  // Check allowed stream time
  const isWithinAllowedISTHours = () => {
    const nowUTC = moment.utc();
    const startUTC = moment
      .utc()
      .startOf("day")
      .add(2, "hours")
      .add(30, "minutes"); // 8:00 AM IST = 2:30 AM UTC
    const endUTC = moment
      .utc()
      .startOf("day")
      .add(14, "hours")
      .add(30, "minutes"); // 8:00 PM IST = 2:30 PM UTC
    return nowUTC.isBetween(startUTC, endUTC);
  };

  if (childOnPause) {
    return (
      <div className="bg-white m-3 p-3 rounded-lg">
        {constants?.cameraPauseTitle}
        <br />
        {constants?.cameraPauseSubtitle}
      </div>
    );
  }

  if (cctvState.centerCctvDown === true) {
    return (
      <div className="bg-white m-3 p-3 rounded-lg">
        <h3>
          Oops! Internet services not working at center. We will notify you once
          internet is up and running.
        </h3>
        <button
          onClick={() => checkCCTVConnection.mutate()}
          disabled={checkCCTVConnection.isLoading}
          className="btn btn-primary"
        >
          {checkCCTVConnection.isLoading ? "Refreshing..." : "Refresh"}
        </button>
      </div>
    );
  }

  if (!activeChild || !liveStreamUrl) {
    return null;
  }

  if (!isWithinAllowedISTHours()) {
    return (
      <div className="text-center m-3 p-3 rounded-lg bg-white">
        <p>Live stream is available only between 8:00 AM and 8:00 PM IST.</p>
      </div>
    );
  }

  // When streamError (invalid auth token), show Refresh Stream button to reload auth
  if (streamError) {
    return (
      <div className="text-center m-3 p-3 rounded-lg bg-white">
        <p>Livestream Not Available. Please Refresh.</p>
        <button
          className="btn btn-danger"
          onClick={() => {
            reloadData.mutate();
          }}
          disabled={reloadData.isLoading}
        >
          {reloadData.isLoading ? "Refreshing Stream..." : "Refresh Stream"}
        </button>
      </div>
    );
  }

  // Otherwise render the stream with a unique key to force reload on refreshKey changes
  return (
    <StyledCard>
      {(checkCCTVConnection.isLoading || reloadData.isLoading) && (
        <SpinnerLoading />
      )}
      <WebView key={refreshKey} url={liveStreamUrl} />
    </StyledCard>
  );
};

export default Cctv;
